import React from 'react';
import { LayoutComposer } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { NamedLink } from '../../components';
import css from './CollaboratorsPage.module.css';

const CollaboratorsPage = () => {
    const layoutAreas = `
        topbar
        main
        footer
    `;

    const collaborationTypes = [
        {
            title: "Restaurantes y Hostelería",
            description: "Accede directamente a productos frescos y locales para tu menú",
            benefits: [
                "Productos de temporada garantizados",
                "Precios competitivos sin intermediarios",
                "Historia detrás de cada ingrediente",
                "Menús con origen certificado"
            ],
            icon: "🍽️"
        },
        {
            title: "Tiendas y Supermercados",
            description: "Amplía tu oferta con productos locales de calidad",
            benefits: [
                "Catálogo de agricultores verificados",
                "Gestión centralizada de pedidos",
                "Información de trazabilidad completa",
                "Marketing de proximidad"
            ],
            icon: "🏪"
        },
        {
            title: "Escuelas y Centros Educativos",
            description: "Educar alimentando con productos de proximidad",
            benefits: [
                "Alimentos frescos para comedores escolares",
                "Actividades educativas con agricultores",
                "Proyectos de huerto escolar",
                "Material didáctico sobre alimentación"
            ],
            icon: "🏫"
        },
        {
            title: "Empresas y Corporaciones",
            description: "Bienestar corporativo con comida local",
            benefits: [
                "Cestas de empresa personalizadas",
                "Servicios de catering local",
                "Eventos corporativos sostenibles",
                "Programas de bienestar"
            ],
            icon: "🏢"
        },
        {
            title: "Asociaciones y Comunidades",
            description: "Fortalece la comunidad con alimentos locales",
            benefits: [
                "Grupos de compra organizada",
                "Eventos comunitarios",
                "Talleres de cocina local",
                "Programas de inclusión social"
            ],
            icon: "🤝"
        },
        {
            title: "Influencers y Creadores",
            description: "Comparte la revolución de la comida local",
            benefits: [
                "Acceso exclusivo a historias de agricultores",
                "Contenido auténtico y verificado",
                "Colaboraciones remuneradas",
                "Visibilidad en la plataforma"
            ],
            icon: "📱"
        }
    ];

    const successStories = [
        {
            name: "Restaurante 'La Tierra'",
            type: "Restaurante",
            location: "Madrid",
            story: "Desde que colaboramos con BioCerca, nuestro menú ha ganado identidad. Nuestros clientes valoran conocer el origen de cada plato y hemos reducido costes un 20%.",
            results: ["20% ahorro en costes", "95% satisfacción cliente", "Menú 100% local"]
        },
        {
            name: "Colegio 'El Roble'",
            type: "Centro Educativo",
            location: "Barcelona",
            story: "Nuestro comedor escolar ahora sirve alimentos de agricultores locales. Los niños aprenden de dónde viene su comida y comen más sano.",
            results: ["100% productos locales", "Actividades educativas", "Menos desperdicio"]
        },
        {
            name: "Supermercado 'Verdú'",
            type: "Tienda",
            location: "Valencia",
            story: "Hemos creado una sección 'BioCerca' que ha aumentado nuestras ventas en un 35%. Los clientes buscan activamente productos con historia.",
            results: ["35% aumento ventas", "Clientes fieles", "Diferenciación mercado"]
        }
    ];

    const processSteps = [
        {
            number: "01",
            title: "Contacto Inicial",
            description: "Cuéntanos tu proyecto y cómo crees que podemos colaborar."
        },
        {
            number: "02",
            title: "Revisión y Propuesta",
            description: "Analizamos tu caso y proponemos un modelo de colaboración adaptado."
        },
        {
            number: "03",
            title: "Acuerdo y Onboarding",
            description: "Formalizamos la colaboración y te integramos en nuestra plataforma."
        },
        {
            number: "04",
            title: "Lanzamiento y Seguimiento",
            description: "Iniciamos la colaboración y acompañamos tu crecimiento con nosotros."
        }
    ];

    return (
        <LayoutComposer areas={layoutAreas} className={css.root}>
            {layoutProps => {
                const { Topbar, Main, Footer } = layoutProps;
                return (
                    <>
                        <Topbar as="header" className={css.topbar}>
                            <TopbarContainer currentPage="CollaboratorsPage" />
                        </Topbar>
                        <Main as="main" className={css.main}>
                            {/* Hero Section */}
                            <section className={css.hero}>
                                <div className={css.heroContainer}>
                                    <div className={css.heroContent}>
                                        <div className={css.breadcrumb}>Colaboraciones</div>
                                        <h1 className={css.heroTitle}>
                                            Conectamos agricultores locales con empresas que valoran la calidad
                                        </h1>
                                        <p className={css.heroSubtitle}>
                                            Únete a la red de colaboradores que está transformando el sistema alimentario 
                                            hacia uno más sostenible, transparente y justo para todos.
                                        </p>
                                        <div className={css.heroStats}>
                                            <div className={css.statItem}>
                                                <div className={css.statNumber}>500+</div>
                                                <div className={css.statLabel}>Agricultores locales</div>
                                            </div>
                                            <div className={css.divider}></div>
                                            <div className={css.statItem}>
                                                <div className={css.statNumber}>50+</div>
                                                <div className={css.statLabel}>Colaboradores activos</div>
                                            </div>
                                            <div className={css.divider}></div>
                                            <div className={css.statItem}>
                                                <div className={css.statNumber}>10K+</div>
                                                <div className={css.statLabel}>Familias impactadas</div>
                                            </div>
                                        </div>
                                        <div className={css.heroCta}>
                                            <NamedLink name="ContactPage" className={css.primaryButton}>
                                                Solicitar información
                                            </NamedLink>
                                          
                                        </div>
                                    </div>
                                    <div className={css.heroImage}>
                                        <div className={css.imagePlaceholder}>
                                            {/* Image would go here */}
                                            <div className={css.imageOverlay}>
                                                <div className={css.imageText}>Agricultura local y sostenible</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Benefits Section */}
                            <section className={css.benefitsSection}>
                                <div className={css.sectionContainer}>
                                    <div className={css.sectionHeader}>
                                        <h2 className={css.sectionTitle}>
                                            <span className={css.titleAccent}>Ventajas</span> de colaborar con BioCerca
                                        </h2>
                                        <p className={css.sectionDescription}>
                                            Ofrecemos más que productos - ofrecemos relaciones directas, transparencia 
                                            y un impacto positivo medible en tu comunidad local.
                                        </p>
                                    </div>
                                    <div className={css.benefitsGrid}>
                                        <div className={css.benefitCard}>
                                            <div className={css.benefitIconContainer}>
                                                <span className={css.benefitIcon}>✓</span>
                                            </div>
                                            <h3 className={css.benefitTitle}>Impacto directo</h3>
                                            <p className={css.benefitText}>
                                                Apoya directamente a agricultores locales, asegurando ingresos justos 
                                                y manteniendo el valor en tu comunidad.
                                            </p>
                                        </div>
                                        <div className={css.benefitCard}>
                                            <div className={css.benefitIconContainer}>
                                                <span className={css.benefitIcon}>♻️</span>
                                            </div>
                                            <h3 className={css.benefitTitle}>Sostenibilidad certificada</h3>
                                            <p className={css.benefitText}>
                                                Reduce la huella de carbono con cadenas cortas de distribución 
                                                y prácticas agrícolas responsables verificadas.
                                            </p>
                                        </div>
                                        <div className={css.benefitCard}>
                                            <div className={css.benefitIconContainer}>
                                                <span className={css.benefitIcon}>📈</span>
                                            </div>
                                            <h3 className={css.benefitTitle}>Diferenciación competitiva</h3>
                                            <p className={css.benefitText}>
                                                Ofrece a tus clientes productos con historia verificada y trazabilidad 
                                                completa que marca la diferencia.
                                            </p>
                                        </div>
                                        <div className={css.benefitCard}>
                                            <div className={css.benefitIconContainer}>
                                                <span className={css.benefitIcon}>🤝</span>
                                            </div>
                                            <h3 className={css.benefitTitle}>Comunidad activa</h3>
                                            <p className={css.benefitText}>
                                                Conecta con una red de profesionales comprometidos con la alimentación 
                                                sostenible y el desarrollo local.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Process Section */}
                            <section className={css.processSection}>
                                <div className={css.sectionContainer}>
                                    <div className={css.sectionHeader}>
                                        <h2 className={css.sectionTitle}>Proceso de <span className={css.titleAccent}>Colaboración</span></h2>
                                        <p className={css.sectionDescription}>
                                            Un camino claro y estructurado hacia una colaboración exitosa y beneficiosa 
                                            para ambas partes.
                                        </p>
                                    </div>
                                    <div className={css.processSteps}>
                                        {processSteps.map((step, index) => (
                                            <div key={index} className={css.processStep}>
                                                <div className={css.stepNumber}>{step.number}</div>
                                                <div className={css.stepContent}>
                                                    <h3 className={css.stepTitle}>{step.title}</h3>
                                                    <p className={css.stepDescription}>{step.description}</p>
                                                </div>
                                                {index < processSteps.length - 1 && (
                                                    <div className={css.stepConnector}></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>

                            {/* CTA Section */}
                            <section className={css.ctaSection}>
                                <div className={css.ctaContainer}>
                                    <div className={css.ctaContent}>
                                        <h2 className={css.ctaTitle}>¿Listo para transformar tu relación con la alimentación local?</h2>
                                        <p className={css.ctaDescription}>
                                            Únete a la red de colaboradores BioCerca y forma parte del cambio hacia 
                                            un sistema alimentario más justo, transparente y sostenible.
                                        </p>
                                        <div className={css.ctaButtons}>
                                            <NamedLink name="ContactPage" className={css.ctaPrimary}>
                                                Contactar para colaborar
                                            </NamedLink>
                                            <NamedLink name="SignupPage" className={css.ctaSecondary}>
                                                Registrarse como colaborador
                                            </NamedLink>
                                        </div>
                                        <p className={css.ctaNote}>
                                            ¿Tienes dudas? <NamedLink name="ContactPage" className={css.inlineLink}>
                                                Contacta con nuestro equipo
                                            </NamedLink> para una consulta personalizada.
                                        </p>
                                    </div>
                                </div>
                            </section>
                        </Main>
                        <Footer>
                            <FooterContainer />
                        </Footer>
                    </>
                );
            }}
        </LayoutComposer>
    );
};

export default CollaboratorsPage;